// 261005 E3-P2b — MỌI quyết định hiển thị của thẻ khối chặng ④ Chia tập (hàm thuần, có jest). KHÔNG import antd/react.
// Hai loại khối: `ops` (cắt/gộp/dời/sửa chữ — xem thử đồng bộ $0, READY ngay) · `rewrite` (AI viết lại tóm tắt/móc — xem thử là JOB nền, tốn AI, người bấm "Xem thử").
import type { AssistantBlock, SplitEpisodeBrief, SplitOpsDiff, SplitRewriteDiff, SplitRewriteRow } from '../../../../types/adaptationAssistant';
import { friendlyJobNote } from '../jobErrorText';
import { autoApplyNote, type AutoApplyNote } from './autoApplyView';

const str = (v: unknown): string => String(v ?? '').trim();
const UNDO_EPS_MS = 1;

export type SplitPhase = 'proposed' | 'ready' | 'stale' | 'failed' | 'applied' | 'restored' | 'dismissed' | 'running';
export type SplitBlockKind = 'ops' | 'rewrite';

export interface SplitRejectedLine { label: string; reason: string }
/** Một dòng trước/sau của bảng tập: tập · độ dài nguồn · chương · tóm tắt rút gọn · móc rút gọn. */
export interface SplitEpisodeLine { ep: number; meta: string; synopsis: string; hook: string }

export interface SplitCardView {
  kind: SplitBlockKind;
  phase: SplitPhase;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  headline: string;
  detail: string;
  opLabels: string[];
  rejected: SplitRejectedLine[];
  /** ops: bảng trước/sau của các tập bị đụng (rỗng nếu chỉ sửa chữ ⇒ dùng `textRows`). */
  before: SplitEpisodeLine[];
  after: SplitEpisodeLine[];
  /** rewrite: từng tập cũ → mới. */
  rewriteRows: SplitRewriteRow[];
  /** Sau khi áp lệnh cấu trúc: nhắc tập bị đụng được đánh dấu cần xem lại. */
  touchedNote: string | null;
  canApply: boolean;
  canPreview: boolean;
  previewLabel: string;
  /** Nhãn chi phí cạnh nút xem thử (rewrite ⇒ "Tốn AI"; ops ⇒ null). */
  previewCost: string | null;
  canUndo: boolean;
  undoReason: string | null;
  undoHint: string;
  canReapply: boolean;
  /** 261015 P5a (D5): "Đã tự áp…" / "Chưa tự áp: …". */
  autoNote: AutoApplyNote | null;
  canDismiss: boolean;
}

export const UNDO_CHANGED_TEXT_SPLIT = 'Chia tập đã đổi sau khi áp — khôi phục qua mục Phiên bản';
export const STALE_TEXT_SPLIT = 'Chia tập đã đổi sau khi xem thử (áp một đề xuất khác, sửa tay, dựng lại…) — xem thử lại để thấy tác động mới';
export const UNDO_HINT_SPLIT = 'Chia tập về bản trước khi áp. Tập đã viết (≤ M) không đổi; nếu bản trước đổi cấu trúc tập đã viết thì server từ chối.';
export const REWRITE_COST_HINT = 'Tốn AI';
export const REWRITE_PROPOSED_TEXT = 'AI sẽ đọc đúng đoạn nguồn của các tập này và viết lại tóm tắt/móc/treo; ranh giới tập giữ nguyên. Bấm "Xem thử" để chạy (tốn AI), rồi bấm Áp nếu ưng.';

/** Loại khối theo `command.kind` (thiếu ⇒ `ops`). */
export const splitKindOf = (block: Pick<AssistantBlock, 'command'>): SplitBlockKind => (block.command?.kind === 'rewrite' ? 'rewrite' : 'ops');

export const splitOpsDiffOf = (block: Pick<AssistantBlock, 'diff'>): SplitOpsDiff | null => {
  const d = block.diff as unknown as Partial<SplitOpsDiff> | null;
  if (!d || typeof d !== 'object' || (d as { kind?: string }).kind === 'rewrite' || !Array.isArray(d.applied)) return null;
  return {
    kind: 'ops', applied: d.applied, rejected: Array.isArray(d.rejected) ? d.rejected : [], touched: Array.isArray(d.touched) ? d.touched : [], structural: d.structural === true,
    violations: Array.isArray(d.violations) ? d.violations : [], outlinePreview: d.outlinePreview && typeof d.outlinePreview === 'object' ? d.outlinePreview : null, lockedThrough: d.lockedThrough,
  };
};

export const splitRewriteDiffOf = (block: Pick<AssistantBlock, 'diff'>): SplitRewriteDiff | null => {
  const d = block.diff as unknown as Partial<SplitRewriteDiff> | null;
  if (!d || typeof d !== 'object' || d.kind !== 'rewrite' || !Array.isArray(d.rows)) return null;
  return { kind: 'rewrite', rows: d.rows, costUsd: typeof d.costUsd === 'number' ? d.costUsd : null };
};

const clip = (s: unknown, n: number): string => { const t = str(s); return t.length > n ? `${t.slice(0, n)}…` : t; };

/** Dòng tập rút gọn: "≈12.300 ký tự nguồn · chương 3, 4". */
export const episodeLineOf = (e: SplitEpisodeBrief): SplitEpisodeLine => {
  const chars = typeof e.sourceChars === 'number' ? `≈${e.sourceChars.toLocaleString('vi-VN')} ký tự nguồn` : '';
  const chs = e.sourceChapters?.length ? `chương ${e.sourceChapters.join(', ')}` : '';
  return { ep: e.ep, meta: [chars, chs].filter(Boolean).join(' · '), synopsis: clip(e.synopsis, 160), hook: clip(e.hook, 100) };
};

export const opLabelsOf = (block: Pick<AssistantBlock, 'command'>): string[] => {
  const ops = block.command?.ops ?? [];
  const labels = block.command?.labels ?? [];
  return ops.map((o, i) => str(labels[i]) || str(o.op));
};

/** Lệnh bị loại: normaliser (`command.rejectedOps`) + xem thử (`diff.rejected`). */
export const splitRejectedLinesOf = (block: Pick<AssistantBlock, 'command' | 'diff'>): SplitRejectedLine[] => [
  ...(block.command?.rejectedOps ?? []).map((r) => ({ label: str(r.op), reason: str(r.reason) })),
  ...(splitOpsDiffOf(block)?.rejected ?? []).map((r) => ({ label: str(r.op), reason: str(r.reason) })),
];

const undoState = (block: AssistantBlock, headModifiedAt: string | null | undefined, busy: boolean, busyReason?: string | null): { canUndo: boolean; undoReason: string | null } => {
  if (busy) return { canUndo: false, undoReason: busyReason || 'Đang có việc khác chạy — chờ xong rồi khôi phục.' };
  const applied = Date.parse(str(block.appliedResult?.modifiedAt));
  const head = Date.parse(str(headModifiedAt));
  if (Number.isNaN(head)) return { canUndo: false, undoReason: 'Đang tải chia tập hiện tại…' };
  if (Number.isNaN(applied) || Math.abs(head - applied) > UNDO_EPS_MS) return { canUndo: false, undoReason: UNDO_CHANGED_TEXT_SPLIT };
  return { canUndo: true, undoReason: null };
};

/**
 * Thẻ khối ④. `busy` ⇒ mọi nút GHI (Xem thử, Áp, Khôi phục, Áp lại) tắt; "Bỏ" không bị khoá.
 * `ops`: PROPOSED hiếm gặp (xem thử tự động chưa chạy được) ⇒ "Xem thử" ($0). `rewrite`: PROPOSED là trạng thái BÌNH THƯỜNG — chờ người bấm "Xem thử (tốn AI)".
 */
export const splitCardView = (block: AssistantBlock, busy: boolean, headModifiedAt?: string | null, busyReason?: string | null): SplitCardView => {
  const kind = splitKindOf(block);
  const opsDiff = kind === 'ops' ? splitOpsDiffOf(block) : null;
  const rwDiff = kind === 'rewrite' ? splitRewriteDiffOf(block) : null;
  const rationale = str(block.command?.rationale);
  const base = {
    kind, opLabels: kind === 'ops' ? opLabelsOf(block) : [], rejected: splitRejectedLinesOf(block),
    before: (opsDiff?.outlinePreview?.before ?? []).map(episodeLineOf), after: (opsDiff?.outlinePreview?.after ?? []).map(episodeLineOf),
    rewriteRows: rwDiff?.rows ?? [], touchedNote: null as string | null,
    canApply: false, canPreview: false, previewLabel: 'Xem thử lại', previewCost: kind === 'rewrite' ? REWRITE_COST_HINT : null,
    canUndo: false, undoReason: null as string | null, undoHint: UNDO_HINT_SPLIT, canReapply: false, canDismiss: false,
    detail: rationale ? `Lý do: ${rationale}` : '', autoNote: autoApplyNote(block),
  };
  const eps = (block.command?.eps ?? []).join(', ');
  switch (block.state) {
    case 'PROPOSED':
      return {
        ...base, phase: 'proposed', tone: 'neutral', headline: kind === 'rewrite' ? `Chưa viết lại${eps ? ` (tập ${eps})` : ''}` : 'Chưa xem thử',
        detail: kind === 'rewrite' ? [REWRITE_PROPOSED_TEXT, base.detail].filter(Boolean).join(' ') : base.detail,
        canPreview: !busy, previewLabel: kind === 'rewrite' ? 'Xem thử (tốn AI)' : 'Xem thử', canDismiss: true,
      };
    case 'PREVIEW_RUNNING':
      return { ...base, phase: 'running', tone: 'neutral', headline: kind === 'rewrite' ? 'AI đang viết lại…' : 'Đang xem thử…', canDismiss: true };   // 261015: BE cho DISMISS từ PREVIEW_RUNNING
    case 'FAILED':
      return { ...base, phase: 'failed', tone: 'danger', headline: kind === 'rewrite' ? 'Viết lại lỗi' : 'Xem thử lỗi', detail: friendlyJobNote(str(block.error)) || base.detail, canPreview: !busy, previewLabel: kind === 'rewrite' ? 'Xem thử lại (tốn AI)' : 'Xem thử lại', canDismiss: true };
    case 'STALE': {
      const reason = str(block.error) || `Đã cũ — tập ${(block.command?.eps ?? []).join(', ') || '?'} đã đổi`;
      return { ...base, phase: 'stale', tone: 'warn', headline: reason, detail: STALE_TEXT_SPLIT, previewLabel: 'Xem thử đã cũ', canDismiss: true };
    }
    case 'READY': {
      const has = kind === 'ops' ? !!opsDiff && opsDiff.applied.length > 0 : !!rwDiff && rwDiff.rows.length > 0;
      const warn = kind === 'ops' && !!opsDiff?.structural;
      return {
        ...base, phase: 'ready', tone: warn ? 'warn' : 'ok', headline: 'Sẵn sàng áp',
        detail: [base.detail, warn ? 'Có đổi cấu trúc tập: các tập bị đụng sẽ được đánh dấu "Cần chấm lại sau khi sửa".' : ''].filter(Boolean).join(' · '),
        canApply: !busy && has, canDismiss: true,
      };
    }
    case 'APPLIED': {
      const v = block.appliedResult?.versionNo;
      const u = undoState(block, headModifiedAt, busy, busyReason);
      const touched = block.appliedResult?.touched ?? [];
      const note = block.appliedResult?.structural && touched.length ? `Tập ${touched.join(', ')} được đánh dấu "Cần chấm lại sau khi sửa".` : null;
      return { ...base, phase: 'applied', tone: 'ok', headline: typeof v === 'number' ? `Đã áp (v${v})` : 'Đã áp', detail: 'Bản cũ vẫn còn ở mục Phiên bản.', touchedNote: note, ...u };
    }
    case 'RESTORED': {
      const rv = block.appliedResult?.restoreVersionNo;
      return {
        ...base, phase: 'restored', tone: 'neutral', headline: typeof rv === 'number' ? `Đã gỡ khỏi chia tập (v${rv})` : 'Đã gỡ khỏi chia tập',
        detail: '"Áp lại" không tốn AI (kể cả chữ AI đã viết) khi phần chia tập chưa khác lúc đề xuất; nếu đã khác thì cần "Xem thử lại".',
        canPreview: !busy, previewLabel: kind === 'rewrite' ? 'Xem thử lại (tốn AI)' : 'Xem thử lại', canReapply: !busy, canDismiss: true,
      };
    }
    default:
      return { ...base, phase: 'dismissed', tone: 'neutral', headline: 'Đã bỏ', detail: '' };
  }
};
